/**
 * Stage definitions for the P9 animated reveal.
 *   Phase 1: dog profile summary (identity, weight, activity, food, health)
 *   Phase 2: calculation results (kcal, grams, recipes, supplements)
 */

import { ActivityLevel, AgeCategory, Pathology } from '../../types';

export interface RevealContext {
  nombre: string;
  raza?: string;
  /** Resolved breed display name from breed_database. Preferred over the
   *  snake_case `raza` key when rendering. */
  breedDisplayName?: string;
  ageYears?: number;
  ageCategory?: AgeCategory;
  gender?: string;
  peso?: number;
  bodyCondition?: string;
  activityLevel?: ActivityLevel;
  currentFood?: string[];
  pathologies?: Pathology[];
  allergies?: string[];
  daily_kcal?: number;
  daily_grams?: number;
  recipe_count?: number;
  supplement_count?: number;
}

export interface RevealStage {
  phase: 1 | 2;
  key: string;
  computeLabel: (ctx: RevealContext) => string;
  delayMs: number;
  durationMs: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function humanizeRazaKey(raza?: string): string | null {
  if (!raza) return null;
  return raza.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  [ActivityLevel.BAJO]: 'tranquila',
  [ActivityLevel.MEDIO]: 'equilibrada',
  [ActivityLevel.ALTO]: 'muy activa',
};

function activityLabel(a?: ActivityLevel): string {
  return (a && ACTIVITY_LABELS[a]) || 'sin especificar';
}

const FOOD_LABELS: Record<string, string> = {
  pienso_seco: 'pienso',
  comida_humeda: 'húmeda',
  comida_cocinada: 'cocinada',
  natural_cocinada: 'cocinada',
  casera: 'casera',
  barf: 'BARF',
  cruda: 'BARF/cruda',
  premios_sobras: 'premios',
  deshidratada: 'deshidratada',
  mixta: 'mixta',
  no_lo_se: 'sin especificar',
};

function foodLabel(k: string): string {
  return FOOD_LABELS[k] ?? k;
}

const PATHOLOGY_LABELS: Record<string, string> = {
  sensibilidad_digestiva: 'Digestión sensible',
  sobrepeso_obesidad: 'Sobrepeso',
  problemas_articulares: 'Articulaciones',
  piel_pelo: 'Piel y pelo',
  dental: 'Salud dental',
  renal: 'Renal',
  hepatica: 'Hepática',
  diabetes: 'Diabetes',
  pancreatica: 'Pancreática',
  cancer: 'Cáncer',
  epilepsia: 'Epilepsia',
  cardiopatias: 'Cardiopatías',
  otra: 'Otra',
  alergias_alimentarias: 'Alergias',
};

function humanizePathology(p: string): string {
  return PATHOLOGY_LABELS[p] || p;
}

function razaEdadLabel(c: RevealContext): string {
  const breedName = c.breedDisplayName ?? humanizeRazaKey(c.raza);
  const isPuppy = c.ageCategory === AgeCategory.CACHORRO;
  const ageLabel = isPuppy
    ? 'cachorro'
    : c.ageYears != null
      ? `${c.ageYears} ${c.ageYears === 1 ? 'año' : 'años'}`
      : null;
  const parts: string[] = [];
  if (breedName) {
    parts.push(ageLabel ? `${breedName} ${isPuppy ? ageLabel : `de ${ageLabel}`}` : breedName);
  } else if (ageLabel) {
    parts.push(ageLabel);
  }
  if (c.gender) parts.push(c.gender);
  return parts.join(', ') || '—';
}

function saludLabel(c: RevealContext): string {
  const real = (c.pathologies || []).filter((x) => x !== Pathology.NINGUNA);
  if (real.length === 0) return 'Sin enfermedades conocidas';
  const names = real.map(humanizePathology);
  // Hasta 2 explícitos; con más, "X y N más" para que quepa en una sola línea.
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} y ${names[1]}`;
  return `${names[0]} y ${names.length - 1} más`;
}

// ── Stage definitions ─────────────────────────────────────────────────────────

export const FASE_1_STAGES: RevealStage[] = [
  {
    phase: 1,
    key: 'raza_edad',
    delayMs: 0,
    durationMs: 150,
    computeLabel: razaEdadLabel,
  },
  {
    phase: 1,
    key: 'peso',
    delayMs: 150,
    durationMs: 150,
    computeLabel: (c) => `${c.peso ?? '?'} kg · condición ${c.bodyCondition || 'sin especificar'}`,
  },
  {
    phase: 1,
    key: 'actividad',
    delayMs: 300,
    durationMs: 150,
    computeLabel: (c) => `Actividad ${activityLabel(c.activityLevel)}`,
  },
  {
    phase: 1,
    key: 'comida',
    delayMs: 450,
    durationMs: 150,
    computeLabel: (c) => `Come ${(c.currentFood || []).map(foodLabel).join(', ') || 'sin especificar'}`,
  },
  {
    phase: 1,
    key: 'salud',
    delayMs: 600,
    durationMs: 150,
    computeLabel: saludLabel,
  },
];

export const FASE_2_STAGES: RevealStage[] = [
  {
    phase: 2,
    key: 'kcal',
    delayMs: 0,
    durationMs: 350,
    computeLabel: (c) => (c.daily_kcal ? `${c.daily_kcal} kcal/día ✓` : 'Calculando kcal diarias…'),
  },
  {
    phase: 2,
    key: 'grams',
    delayMs: 350,
    durationMs: 350,
    computeLabel: (c) => (c.daily_grams ? `${c.daily_grams} g/día ✓` : 'Calculando ración diaria…'),
  },
  {
    phase: 2,
    key: 'recipes',
    delayMs: 700,
    durationMs: 350,
    computeLabel: (c) =>
      c.recipe_count != null ? `${c.recipe_count} recetas para ${c.nombre} ✓` : 'Filtrando recetas compatibles…',
  },
  {
    phase: 2,
    key: 'supplements',
    delayMs: 1050,
    durationMs: 350,
    computeLabel: (c) =>
      c.supplement_count != null ? `${c.supplement_count} suplementos sugeridos ✓` : 'Analizando suplementos…',
  },
];

// ── Timing constants ──────────────────────────────────────────────────────────

/** Duration of phase 1 (profile items reveal).
 * 850 ms = last stage fires at 750 ms + 100 ms buffer to avoid a race
 * condition where the phase-1 timer and the phase-transition timer fire
 * at the exact same tick. */
export const PHASE_1_DURATION_MS = 850;

/** Duration of phase 2 (calculation results reveal). */
export const PHASE_2_DURATION_MS = 1500;

/** Backend timeout: if calculation takes longer than this, show fallback text. */
export const CALC_TIMEOUT_MS = 6000;
